import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PublishingSettings = ({ settings, onUpdate, onPreview }) => {
  const [formData, setFormData] = useState(settings);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onUpdate(formData);
  };

  const trimSizeOptions = [
    { value: '5x8', label: '5" x 8" (Standard)' },
    { value: '5.5x8.5', label: '5.5" x 8.5" (US Trade)' },
    { value: '6x9', label: '6" x 9" (US Trade)' },
    { value: '7x10', label: '7" x 10" (Crown Quarto)' },
    { value: '8.5x11', label: '8.5" x 11" (US Letter)' }
  ];

  const categoryOptions = [
    { value: 'business', label: 'Business & Money' },
    { value: 'self-help', label: 'Self-Help' },
    { value: 'biography', label: 'Biography & Memoir' },
    { value: 'marketing', label: 'Marketing & Sales' },
    { value: 'leadership', label: 'Leadership' }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Book Metadata
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <Input
              label="Book Title"
              type="text"
              value={formData?.title}
              onChange={(e) => handleChange('title', e?.target?.value)}
              required
            />

            <Input
              label="Subtitle"
              type="text"
              value={formData?.subtitle}
              onChange={(e) => handleChange('subtitle', e?.target?.value)}
            />

            <Input
              label="Author Name"
              type="text"
              value={formData?.author}
              onChange={(e) => handleChange('author', e?.target?.value)}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="ISBN (Optional)"
                type="text"
                value={formData?.isbn}
                onChange={(e) => handleChange('isbn', e?.target?.value)}
                placeholder="978-0-00-000000-0"
              />

              <Input
                label="Publication Date"
                type="date"
                value={formData?.publicationDate}
                onChange={(e) => handleChange('publicationDate', e?.target?.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Book Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                rows={6}
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                placeholder="Enter a compelling book description..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData?.description?.length} / 4000 characters
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Categories & Keywords
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <Select
              label="Primary Category"
              options={categoryOptions}
              value={formData?.primaryCategory}
              onChange={(value) => handleChange('primaryCategory', value)}
              required
            />

            <Select
              label="Secondary Category"
              options={categoryOptions}
              value={formData?.secondaryCategory}
              onChange={(value) => handleChange('secondaryCategory', value)}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Keywords (7 maximum)
              </label>
              <div className="space-y-2">
                {formData?.keywords?.map((keyword, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="text"
                      value={keyword}
                      onChange={(e) => {
                        const newKeywords = [...formData?.keywords];
                        newKeywords[index] = e?.target?.value;
                        handleChange('keywords', newKeywords);
                      }}
                      placeholder={`Keyword ${index + 1}`}
                    />
                    {formData?.keywords?.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="X"
                        onClick={() => {
                          const newKeywords = formData?.keywords?.filter((_, i) => i !== index);
                          handleChange('keywords', newKeywords);
                        }}
                      />
                    )}
                  </div>
                ))}
                {formData?.keywords?.length < 7 && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => handleChange('keywords', [...formData?.keywords, ''])}
                  >
                    Add Keyword
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              Formatting Options
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <Select
              label="Print Trim Size"
              options={trimSizeOptions}
              value={formData?.trimSize}
              onChange={(value) => handleChange('trimSize', value)}
              description="Standard size for your book genre"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Interior Type"
                type="text"
                value={formData?.interiorType}
                disabled
              />

              <Input
                label="Paper Type"
                type="text"
                value={formData?.paperType}
                disabled
              />
            </div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-1 space-y-6">
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Publishing Checklist
            </h3>
          </div>
          <div className="p-4 space-y-3">
            {[
              { label: 'Manuscript Complete', completed: true },
              { label: 'Cover Design Finalized', completed: true },
              { label: 'Metadata Filled', completed: formData?.title && formData?.author },
              { label: 'Categories Selected', completed: formData?.primaryCategory },
              { label: 'Keywords Added', completed: formData?.keywords?.filter(k => k)?.length >= 5 },
              { label: 'ISBN Assigned', completed: formData?.isbn },
              { label: 'Preview Generated', completed: false }
            ]?.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                  item?.completed ? 'bg-success' : 'bg-muted'
                }`}>
                  {item?.completed && (
                    <Icon name="Check" size={12} className="text-success-foreground" />
                  )}
                </div>
                <span className={`text-sm ${
                  item?.completed ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {item?.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Quick Actions
            </h3>
          </div>
          <div className="p-4 space-y-2">
            <Button
              variant="outline"
              size="default"
              fullWidth
              iconName="Eye"
              iconPosition="left"
              onClick={onPreview}
            >
              Preview Book
            </Button>
            <Button
              variant="default"
              size="default"
              fullWidth
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Settings
            </Button>
          </div>
        </div>

        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                Ready to Publish?
              </p>
              <p className="text-xs text-muted-foreground">
                Complete all checklist items to enable publishing to Amazon KDP.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishingSettings;